var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from '../framework/Timeouts';
import { TransportError } from '../framework/Transport';
export class NanolumensCaptivateBase {
    constructor(_driverType, state, log, framework) {
        this._driverType = _driverType;
        this.state = state;
        this.framework = framework;
        // static properties
        // other class properties and constructor
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // An LED wall's power state can either be controled with power commands directly, or use the
        // brightness level to to mimic turning off and on.
        this.useBrightnessAsPower = false;
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.useBrightnessAsPower) {
                    const match = yield this.send(this.makeCommand([0x1d, 0xc2], []), /\x55{7}\xC0\x01\x03\x00\xD1\x01\xD0\x1E\xC2\x00\x00\xFF{17}\x00\x01\x00([\S\s])[\s\S]/, tags);
                    return match[1].charCodeAt(0) > 0; // 0 brightness = power off
                }
                else {
                    const match = yield this.send(this.makeCommand([0x05, 0xc0], []), /\x55{7}\xC0\x01\x03\x00\xD1\x01\xD0\x06\xC0\x00\x00\xFF{17}\x00\x01\x00([\S\s])[\s\S]/, tags);
                    return match[1].charCodeAt(0) === 0x80; // 0x80 is power on
                }
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.useBrightnessAsPower) {
                    // Set brightness level to 0 for power off and 90 for power on.
                    const brightLevel = power ? 0x5a : 0; // ranges from 0 to 100
                    yield this.send(this.makeCommand([0x1f, 0xc2], [brightLevel]), /\x55{7}\xC0\x01\x03\x00\xD1\x01\xD0\x20\xC2\x00\x00\xFF{17}\x00\x02\x00\x01\x00[\s\S]/, tags);
                    return power;
                }
                else {
                    // According to Nanolumens tech support, it can take 30 seconds when powering on the
                    // LED wall before the display is responsive.
                    const suspendMs = power ? 30000 : 2000;
                    this.framework.suspendPolling(suspendMs);
                    const match = yield this.send(this.makeCommand([0x03, 0xc0], [power ? 0x5e : 0x5f]), /\x55{7}\xC0\x01\x03\x00\xD1\x01\xD0\x04\xC0\x00\x00\xFF{17}\x00\x01\x00([\S\s])[\s\S]/, tags);
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), power ? 30000 : 2000);
                    this.framework.suspend(suspendMs);
                    return match[1].charCodeAt(0) === 0x5e;
                }
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.send(this.makeCommand([0x11, 0xc2], []), /\x55{7}\xC0\x01\x03\x00\xD1\x01\xD0\x12\xC2\x00\x00\xFF{17}\x00\x01\x00([\S\s])[\s\S]/, tags);
                return match[1].charCodeAt(0).toString();
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.send(this.makeCommand([0x13, 0xc2], [parseInt(code, 10)]), /\x55{7}\xC0\x01\x03\x00\xD1\x01\xD0\x14\xC2\x00\x00\xFF{17}\x00\x02\x00[\S\s]{3}/, tags);
                return code;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 5000, onePerInterval: true, controls: ['power', 'videoInput'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.send(this.makeCommand([0x01, 0xc0], []), /\x55{7}\xC0\x01\x03\x00\xD1\x01\xD0\x02\xc0/, ['initialize']);
                return 'connected';
            }
            catch (error) {
                if (error instanceof TransportError && error.code === 'sendTimeout')
                    return 'connectFailed';
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    makeCommand(cmd, data) {
        const command = [
            0x55,
            0x55,
            0x55,
            0x55,
            0x55,
            0x55,
            0x55,
            0xc0,
            //--
            0x01,
            0x03,
            //--
            0x01,
            0xd0,
            //--
            0x00,
            0xd1,
            //--
            ...cmd,
            //--
            0x00,
            0x00,
            //--
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            0xff,
            //--
            0x00,
            //--
            data.length & 0xff,
            (data.length >> 8) & 0xff,
            //--
            ...data,
        ];
        let checksum = 0; // checksum from byte 8 on
        for (let i = 8; i < command.length; i++) {
            checksum += command[i];
        }
        command.push(checksum & 0x00ff);
        return command;
    }
}
